/**
 * Live world map showing global average ratings for all countries.
 * Countries are colored from red (low ratings ~1) to blue (high ratings ~5).
 */

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  type VirtualElement,
} from '@floating-ui/react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { zoom as d3Zoom, type ZoomBehavior, zoomIdentity } from 'd3-zoom';
import { ChevronLeft, Globe, Radio, RotateCcw, Users, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { feature, mesh } from 'topojson-client';
import type { GeometryCollection } from 'topojson-specification';
import { Button } from '@/components/ui/button';
import { atlasNameToCode, countries, countryDetails } from '@/data/countries';
import { loadWorldData } from '@/lib/map-data';
import { useLeaderboardSocket } from '@/hooks/useLeaderboardSocket';

interface LiveMapPageProps {
  onBack: () => void;
}

/**
 * Rating colors matching the vote buttons (from CountryCard):
 * 1 = red, 2 = orange, 3 = amber, 4 = lime, 5 = emerald
 */
const RATING_COLORS = [
  { r: 220, g: 38, b: 38 },   // 1: red-600 (#dc2626)
  { r: 249, g: 115, b: 22 },  // 2: orange-500 (#f97316)
  { r: 245, g: 158, b: 11 },  // 3: amber-500 (#f59e0b)
  { r: 101, g: 163, b: 13 },  // 4: lime-600 (#65a30d)
  { r: 5, g: 150, b: 105 },   // 5: emerald-600 (#059669)
];

/**
 * Convert average rating to a color matching the vote button colors.
 * Supports legacy 0-1 scale votes (converts to 1-5) and interpolates between rating colors.
 */
function getScoreColor(avgRating: number | undefined): string {
  if (avgRating === undefined) {
    return '#e8e4df'; // Warm gray for unrated
  }

  // Handle legacy 0-1 scale votes: convert to 1-5 scale
  // If the value is <= 1, treat it as 0-1 scale and convert
  let score = avgRating;
  if (score <= 1) {
    score = 1 + score * 4; // 0->1, 0.5->3, 1->5
  }

  // Clamp between 1 and 5
  score = Math.max(1, Math.min(5, score));

  // Get the two colors to interpolate between
  const lowerIndex = Math.floor(score) - 1; // 0-4
  const upperIndex = Math.min(lowerIndex + 1, 4);
  const t = score - Math.floor(score); // fractional part

  const c1 = RATING_COLORS[lowerIndex];
  const c2 = RATING_COLORS[upperIndex];

  // Interpolate between the two colors
  const r = Math.round(c1.r + (c2.r - c1.r) * t);
  const g = Math.round(c1.g + (c2.g - c1.g) * t);
  const b = Math.round(c1.b + (c2.b - c1.b) * t);

  return `rgb(${r}, ${g}, ${b})`;
}

export function LiveMapPage({ onBack }: LiveMapPageProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const hoverBorderRef = useRef<any>(null);

  const [hoveredCountry, setHoveredCountry] = useState<{
    name: string;
    code: string;
    avgRating?: number;
    totalVotes?: number;
  } | null>(null);
  const [virtualElement, setVirtualElement] = useState<VirtualElement | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Use WebSocket for live leaderboard data
  const { leaderboard, summary, connected } = useLeaderboardSocket();

  // Build a lookup map from country code to stats
  const statsMap = useMemo(() => {
    const map: Record<string, { avgRating: number; totalVotes: number }> = {};
    for (const entry of leaderboard) {
      map[entry.countryCode] = {
        avgRating: entry.avgRating,
        totalVotes: entry.totalVotes,
      };
    }
    return map;
  }, [leaderboard]);

  // Floating UI for tooltip
  const { refs, floatingStyles } = useFloating({
    elements: {
      reference: virtualElement as any,
    },
    placement: 'right',
    middleware: [offset(10), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // Reset zoom handler
  const handleResetZoom = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = select(svgRef.current);
    svg
      .transition()
      .duration(300)
      .call(zoomRef.current.transform as any, zoomIdentity);
    setZoomLevel(1);
  }, []);

  // Render the map
  useEffect(() => {
    if (!svgRef.current) return;

    const width = 1200;
    const height = 600;

    select(svgRef.current).selectAll('*').remove();

    const svg = select(svgRef.current)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto')
      .style('touch-action', 'none');

    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 20])
      .translateExtent([
        [-100, -100],
        [width + 100, height + 100],
      ])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
      });

    zoomRef.current = zoom;
    svg.call(zoom);

    // Load map data
    loadWorldData('50m')
      .then((world) => {
        if (!world) throw new Error('No data received');

        const features = feature(world, world.objects.countries) as any;

        const projection = geoNaturalEarth1()
          .scale(width / 5)
          .translate([width / 2, height / 2]);

        const path = geoPath().projection(projection);

        // Render countries
        g.append('g')
          .attr('class', 'countries')
          .selectAll('path')
          .data(features.features)
          .join('path')
          .attr('d', path as any)
          .attr('fill', (d: any) => {
            const countryName = d.properties.name;
            const countryCode = atlasNameToCode[countryName];
            const stats = countryCode ? statsMap[countryCode] : undefined;
            return getScoreColor(stats?.avgRating);
          })
          .attr('stroke', 'none')
          .attr('class', 'transition-colors duration-200 cursor-pointer')
          .on('mouseenter', (event, d: any) => {
            const countryName = d.properties.name;
            const countryCode = atlasNameToCode[countryName];
            const stats = countryCode ? statsMap[countryCode] : undefined;

            // Update hover border
            if (hoverBorderRef.current) {
              hoverBorderRef.current
                .datum(d)
                .attr('d', path as any)
                .attr('stroke', '#000')
                .attr('stroke-width', 1.5)
                .attr('vector-effect', 'non-scaling-stroke');
            }

            const rect = {
              x: event.clientX,
              y: event.clientY,
              width: 0,
              height: 0,
              top: event.clientY,
              right: event.clientX,
              bottom: event.clientY,
              left: event.clientX,
            };

            setVirtualElement({
              getBoundingClientRect: () => rect as DOMRect,
            });

            setHoveredCountry({
              name: countryName,
              code: countryCode,
              avgRating: stats?.avgRating,
              totalVotes: stats?.totalVotes,
            });
          })
          .on('mousemove', (event) => {
            const rect = {
              x: event.clientX,
              y: event.clientY,
              width: 0,
              height: 0,
              top: event.clientY,
              right: event.clientX,
              bottom: event.clientY,
              left: event.clientX,
            };

            setVirtualElement({
              getBoundingClientRect: () => rect as DOMRect,
            });
          })
          .on('mouseleave', () => {
            if (hoverBorderRef.current) {
              hoverBorderRef.current.attr('stroke', 'none');
            }
            setHoveredCountry(null);
            setVirtualElement(null);
          });

        // Render borders
        const borders = mesh(
          world,
          world.objects.countries as GeometryCollection,
          (a, b) => a !== b,
        );

        g.append('path')
          .datum(borders)
          .attr('d', path as any)
          .attr('fill', 'none')
          .attr('stroke', '#a8a198')
          .attr('stroke-width', 0.5)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('vector-effect', 'non-scaling-stroke')
          .style('pointer-events', 'none');

        // Hover border layer
        hoverBorderRef.current = g
          .append('path')
          .attr('fill', 'none')
          .attr('stroke', 'none')
          .attr('stroke-width', 1.5)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('vector-effect', 'non-scaling-stroke')
          .style('pointer-events', 'none');
      })
      .catch((error) => {
        console.error('Failed to load map data:', error);
      });
  }, [statsMap]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-display text-xl sm:text-2xl flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-500" />
                Live World Map
              </h1>
              <p className="text-xs text-muted-foreground hidden sm:block">
                Global average ratings in real-time
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {connected ? (
              <>
                <Radio className="w-4 h-4 text-green-500 animate-pulse" />
                <span className="hidden sm:inline">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-yellow-500" />
                <span className="hidden sm:inline">Connecting...</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Summary stats */}
      {summary && (
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>
                <strong className="text-foreground">{summary.totalUsers.toLocaleString()}</strong> participants
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span>
                <strong className="text-foreground">{summary.totalRatings.toLocaleString()}</strong> ratings
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span>
                <strong className="text-foreground">{summary.countriesRated}</strong> countries rated
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Map */}
      <main className="max-w-6xl mx-auto px-4 pb-8">
        <div className="bg-stone-100 border border-stone-200 rounded-xl overflow-hidden">
          <svg ref={svgRef} className="w-full h-auto min-h-[400px] lg:min-h-[500px]" />
        </div>

        {/* Controls */}
        <div className="mt-4 flex flex-wrap items-center gap-4 justify-between">
          {/* Zoom controls */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono">
              Zoom: {zoomLevel.toFixed(1)}x
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetZoom}
              disabled={zoomLevel === 1}
              className="h-8 text-xs"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              Reset
            </Button>
          </div>

          {/* Legend - 5 rating colors */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Rating:</span>
            {[1, 2, 3, 4, 5].map((rating) => (
              <div key={rating} className="flex items-center gap-0.5">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getScoreColor(rating) }}
                />
                <span className="text-xs">{rating}</span>
              </div>
            ))}
            <div className="flex items-center gap-1 ml-2">
              <div
                className="w-4 h-4 rounded border border-border"
                style={{ backgroundColor: getScoreColor(undefined) }}
              />
              <span className="text-xs text-muted-foreground">No data</span>
            </div>
          </div>

          {/* Instructions */}
          <span className="text-xs text-muted-foreground hidden md:block">
            Scroll to zoom | Drag to pan
          </span>
        </div>
      </main>

      {/* Tooltip */}
      {hoveredCountry && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="pointer-events-none z-50 p-0 rounded-lg shadow-xl border-2 border-border backdrop-blur-md bg-card/95 overflow-hidden max-w-[240px]"
        >
          <div className="px-3 py-2 border-b border-border/50 bg-muted/20">
            <div className="font-display text-base font-semibold">
              {hoveredCountry.name}
            </div>
            {hoveredCountry.avgRating !== undefined ? (
              <div className="flex items-center gap-2 mt-1">
                <div
                  className="w-3 h-3 rounded-full border border-white/20 shadow-sm"
                  style={{ backgroundColor: getScoreColor(hoveredCountry.avgRating) }}
                />
                <span className="text-sm font-bold" style={{ color: getScoreColor(hoveredCountry.avgRating) }}>
                  {hoveredCountry.avgRating.toFixed(2)}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({hoveredCountry.totalVotes} {hoveredCountry.totalVotes === 1 ? 'vote' : 'votes'})
                </span>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground mt-1">
                No ratings yet
              </div>
            )}
          </div>

          {hoveredCountry.code && countryDetails[hoveredCountry.code] && (
            <div className="p-3 text-xs space-y-1.5">
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Capital</span>
                <span className="font-medium">
                  {countryDetails[hoveredCountry.code].capital}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Population</span>
                <span className="font-medium">
                  {countryDetails[hoveredCountry.code].population}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
